// Page-level glue between the public page configuration and the renderer:
//   usePageDynamicSwipers(page)  -> ONE cached request for all of a page's
//                                   swipers (never one per swiper)
//   <DynamicSwiperSlot>          -> renders the swipers of one slot
// Used by DynamicPageRenderer, the single renderer shared by Home, the
// category pages and every other registered page -- no page-specific copy.
import React, { memo, useEffect, useMemo, useState } from "react";
import { getPublicPageSwipers } from "../auth/apiDynamicSwipers";
import { groupPlacementsBySlot, pageCacheKey } from "./dynamicSwiperRegistry";
import DynamicSwiper from "./DynamicSwiper";

const EMPTY = [];

export const usePageDynamicSwipers = (page) => {
  const key = pageCacheKey(page);
  const [state, setState] = useState({ key: "", placements: EMPTY });

  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    getPublicPageSwipers(page).then(({ placements }) => {
      if (!cancelled) setState({ key, placements: placements.length ? placements : EMPTY });
    });
    return () => { cancelled = true; };
    // `key` fully identifies `page`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Never show the previous page's swipers while the next page loads.
  return state.key === key ? state.placements : EMPTY;
};

export const usePageSwiperSlots = (page, sectionIds) => {
  const placements = usePageDynamicSwipers(page);
  const sectionKey = (sectionIds || []).join("|");
  return useMemo(
    () => {
      const { bySlot, orphans } = groupPlacementsBySlot(placements, sectionIds || []);
      // The first swiper in render order is the only one loaded eagerly.
      const firstId = (bySlot.get("top") || [])[0]?.id || null;
      return { bySlot, orphans, firstId, hasAny: placements.length > 0 };
    },
    // sectionKey is the stable identity of sectionIds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [placements, sectionKey]
  );
};

export const DynamicSwiperSlot = memo(({ slotKey, placements, firstId = null }) => {
  if (!placements || !placements.length) return null;
  return (
    <div className="dsw-slot" data-dsw-slot={slotKey}>
      {placements.map((placement) => (
        <DynamicSwiper key={placement.id} swiper={placement.swiper} eager={placement.id === firstId} />
      ))}
    </div>
  );
});
DynamicSwiperSlot.displayName = "DynamicSwiperSlot";

export default DynamicSwiperSlot;
